import { CAPABILITY_LEVELS } from '@iculogbook/shared';
import type {
  CapabilityBucket,
  DayActivity,
  HilloCoverage,
  TemporalDistribution,
} from '@iculogbook/shared';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { cx } from '../../lib/cx.ts';
import { useChartTheme } from './chartTheme.ts';

/**
 * The trainee dashboard's charts — and, where the shape genuinely matches,
 * the operator metrics page's too (`ActivityHeatmap`, `Doughnut`,
 * `CumulativeChart`, `SimpleBarChart`). One set of primitives rather than a
 * second copy for the admin-only page: none of them know anything about
 * whose data they are drawing, only the generic `{label, count}` /
 * `{date, count}` shapes `aggregate.ts` and `adminMetrics.ts` both produce.
 *
 * Two rules run through all of them. Colours come from the design tokens, never
 * from Recharts' defaults or a literal. And **no series is distinguished by
 * colour alone** — every one carries a label, a value, or both, because a
 * supervisor may well print this in greyscale and because roughly one in twelve
 * men cannot separate the reds from the greens.
 */

const AXIS_STYLE = { fontSize: 11 };

/**
 * The palette wraps rather than running out. `noUncheckedIndexedAccess` cannot
 * see that a modulo index is always in bounds, so it is narrowed here once
 * instead of at every call site.
 */
function sliceColour(theme: { categorical: string[] }, index: number): string {
  return theme.categorical[index % theme.categorical.length] ?? theme.categorical[0] ?? '#5b93d1';
}

/**
 * A token's hex value as an `rgb(… / a)` string.
 *
 * Needed because the shaded cells of the out-of-hours table vary their tint by
 * value, and the tint has to be a background colour rather than an element
 * opacity — see the note at the call site. Falls back to the colour unchanged
 * if it is not the six-digit hex the tokens are written in.
 */
function withAlpha(colour: string, alpha: number): string {
  const hex = colour.trim();
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return hex;

  const channels = [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16));
  return `rgb(${channels.join(' ')} / ${String(Math.round(alpha * 1000) / 1000)})`;
}

/** A quarter key of `2026-Q1` reads better as `Q1 26` on a phone axis. */
function shortQuarter(key: string): string {
  const [year, quarter] = key.split('-');
  return `${quarter ?? ''} ${(year ?? '').slice(2)}`;
}

/** `2026-03` reads better as `Mar 26` on a phone axis. Exported for the admin page's "Accounts over time". */
export function shortMonth(key: string): string {
  const [year, month] = key.split('-');
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${names[Number(month) - 1] ?? ''} ${(year ?? '').slice(2)}`;
}

const SHORT_DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });

/** `2026-01-12` reads better as `12 Jan` on a phone axis. */
export function shortDay(isoDate: string): string {
  return SHORT_DAY.format(new Date(`${isoDate}T00:00:00`));
}

/**
 * Capability level over time — the chart that tells the training story, and the
 * reason the dashboard exists at all. A trainee's evidence of progression is
 * the shift of this stack from the pale colours to the strong ones.
 */
export function CapabilityProgression({ buckets }: { buckets: readonly CapabilityBucket[] }) {
  const theme = useChartTheme();

  const data = buckets.map((bucket) => ({
    key: shortQuarter(bucket.key),
    'Level 1': bucket.byLevel[0],
    'Level 2': bucket.byLevel[1],
    'Level 3': bucket.byLevel[2],
    'Level 4': bucket.byLevel[3],
  }));

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <XAxis dataKey="key" stroke={theme.axis} tick={{ ...AXIS_STYLE, fill: theme.muted }} />
          <YAxis
            stroke={theme.axis}
            tick={{ ...AXIS_STYLE, fill: theme.muted }}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: theme.surface,
              border: `1px solid ${theme.grid}`,
              borderRadius: 8,
              fontSize: 12,
            }}
            labelStyle={{ color: theme.text }}
            cursor={{ fill: theme.grid, opacity: 0.4 }}
          />
          <Legend wrapperStyle={{ fontSize: 12, color: theme.muted }} />
          {CAPABILITY_LEVELS.map((level) => (
            <Bar
              key={level}
              dataKey={`Level ${String(level)}`}
              stackId="capability"
              fill={theme.capability[level - 1]}
              // The legend carries the label, so the colour is never the only
              // thing telling a reader which level a band is.
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export type Slice = { label: string; count: number };

/**
 * A doughnut with its own legend, listing every slice with its count.
 *
 * The list is not decoration: it is what makes the chart readable without
 * colour, and on a phone it is often easier to read than the ring itself.
 */
export function Doughnut({
  title,
  slices,
  total,
}: {
  title: string;
  slices: readonly Slice[];
  total: number;
}) {
  const theme = useChartTheme();

  if (slices.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-text">{title}</h3>
        <p className="text-xs text-faint">Nothing in this period.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-text">{title}</h3>

      <div className="h-40 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={[...slices]}
              dataKey="count"
              nameKey="label"
              innerRadius="55%"
              outerRadius="85%"
              stroke={theme.surface}
              isAnimationActive={false}
            >
              {slices.map((slice, index) => (
                <Cell key={slice.label} fill={sliceColour(theme, index)} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: theme.surface,
                border: `1px solid ${theme.grid}`,
                borderRadius: 8,
                fontSize: 12,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <ul className="flex flex-col gap-1">
        {slices.map((slice, index) => (
          <li key={slice.label} className="flex items-center gap-2 text-xs">
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full"
              style={{ background: sliceColour(theme, index) }}
            />
            <span className="min-w-0 flex-1 truncate text-muted">{slice.label}</span>
            <span className="text-text">{slice.count}</span>
            <span className="w-9 text-right text-faint">
              {total === 0 ? '' : `${String(Math.round((slice.count / total) * 100))}%`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A running total, monthly. Takes only `key` and `cumulative` — a strict
 * subset of `CumulativePoint`, so the trainee dashboard's points still fit
 * unchanged, and the admin page's `cumulativeAccounts()` rows (which have no
 * per-month `count`, only the running `total`) fit too without inventing one.
 */
export function CumulativeChart({ points }: { points: readonly { key: string; cumulative: number }[] }) {
  const theme = useChartTheme();
  const data = points.map((point) => ({ key: shortMonth(point.key), Entries: point.cumulative }));

  return (
    <div className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
          <XAxis dataKey="key" stroke={theme.axis} tick={{ ...AXIS_STYLE, fill: theme.muted }} />
          <YAxis
            stroke={theme.axis}
            tick={{ ...AXIS_STYLE, fill: theme.muted }}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: theme.surface,
              border: `1px solid ${theme.grid}`,
              borderRadius: 8,
              fontSize: 12,
            }}
            labelStyle={{ color: theme.text }}
          />
          <Line
            type="monotone"
            dataKey="Entries"
            stroke={theme.capability[2]}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Magnitude by category or by discrete time bucket — one series, so one hue,
 * never a colour per bar. Rounded data-ends anchored to the baseline, an
 * axis and gridline in place of the admin page's old unlabelled columns.
 */
export function SimpleBarChart({
  bars,
  formatLabel = (label: string) => label,
}: {
  bars: readonly { label: string; value: number }[];
  /** E.g. turning `2026-01-12` into `12 Jan` for the axis. */
  formatLabel?: (label: string) => string;
}) {
  const theme = useChartTheme();
  const data = bars.map((bar) => ({ key: formatLabel(bar.label), Count: bar.value }));

  return (
    <div className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
          <CartesianGrid vertical={false} stroke={theme.grid} />
          <XAxis dataKey="key" stroke={theme.axis} tick={{ ...AXIS_STYLE, fill: theme.muted }} />
          <YAxis
            stroke={theme.axis}
            tick={{ ...AXIS_STYLE, fill: theme.muted }}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: theme.grid }}
            contentStyle={{
              background: theme.surface,
              border: `1px solid ${theme.grid}`,
              borderRadius: 8,
              fontSize: 12,
            }}
            labelStyle={{ color: theme.text }}
          />
          <Bar
            dataKey="Count"
            fill={theme.capability[2]}
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const CELL_DATE = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Fixed thresholds, not a scale relative to this trainee's own busiest day.
 *
 * A single admission logged with four procedures would otherwise make every
 * ordinary day look pale by comparison, which is a worse picture of
 * consistency than an absolute scale that means the same thing every time
 * someone looks at it.
 */
function activityLevel(count: number): 0 | 1 | 2 | 3 | 4 {
  if (count <= 0) return 0;
  if (count === 1) return 1;
  if (count === 2) return 2;
  if (count <= 4) return 3;
  return 4;
}

const LEVEL_ALPHA: Record<0 | 1 | 2 | 3 | 4, number> = {
  0: 0,
  1: 0.2,
  2: 0.4,
  3: 0.62,
  4: 0.85,
};

/**
 * A calendar of dots, shaded by how much was logged each day — the trailing
 * twenty weeks, always, whatever the period selector above it is set to.
 *
 * Deliberately not a streak counter. `PRD.md` rules out streaks and
 * gamification outright, and a "days without a break" figure would put a
 * number on something that is often a doctor's annual leave, a run of nights,
 * or a placement outside ICM — none of which this app has any business
 * treating as a lapse. This shows density and nothing else: no target, no
 * "missed" framing, the same restraint the rest of the dashboard keeps.
 *
 * **Fills the card's width exactly, by construction rather than by picking
 * pixel sizes.** Two earlier attempts at this — fixed-size dots sized to
 * "twenty weeks", then re-sized to fit a guessed worst case — both failed on
 * a real phone, because `dayGrid` Monday-aligns its start: the rendered
 * column count is `weeks` on a day when the window's start already falls on
 * a Monday and `weeks + 1` on every other day (a ragged leading part-week
 * gets padded back to the Monday before it), so no fixed dot size is ever
 * exactly right for every day of the week. This version does not pick a dot
 * size at all: it is a CSS grid with one `auto`-width track for the day
 * labels and one `1fr` track per week, so the grid always resolves to
 * exactly the container's width — full, never overflowing — whatever the
 * column count happens to be today. `grid-auto-flow: column` is what makes
 * this work without a wrapper element per week: a flat, column-major list of
 * cells (all seven label cells, then all seven of week one's cells, then
 * week two's, ...) lays itself out in columns on its own. Each dot is
 * `aspect-square`, so its height always matches its width whatever that
 * width resolves to.
 */
export function ActivityHeatmap({ days }: { days: readonly DayActivity[] }) {
  const theme = useChartTheme();
  const base = theme.capability[2];

  const weeks: DayActivity[][] = [];
  for (let index = 0; index < days.length; index += 7) {
    weeks.push(days.slice(index, index + 7));
  }

  const loggedDays = days.filter((day) => day.count > 0).length;

  return (
    <div className="flex flex-col gap-2">
      <p className="sr-only">
        {loggedDays} of the last {days.length} days have at least one entry logged.
      </p>

      <div
        className="grid gap-[2px] pb-1"
        style={{
          gridTemplateColumns: 'auto',
          gridAutoColumns: 'minmax(0, 1fr)',
          gridAutoFlow: 'column',
          gridTemplateRows: 'repeat(7, auto)',
        }}
        aria-hidden="true"
      >
        {DAY_LABELS.map((label, index) => (
          <span
            key={label}
            className="flex items-center pr-1 text-[9px] leading-none text-faint"
            style={{ visibility: index % 2 === 0 ? 'visible' : 'hidden' }}
          >
            {label}
          </span>
        ))}

        {weeks.map((week) =>
          week.map((day) => {
            const level = activityLevel(day.count);
            return (
              <div
                key={day.date}
                title={`${String(day.count)} ${day.count === 1 ? 'entry' : 'entries'} — ${CELL_DATE.format(new Date(day.date))}`}
                className="aspect-square w-full rounded-[2px]"
                style={{
                  backgroundColor: level === 0 ? theme.grid : withAlpha(base, LEVEL_ALPHA[level]),
                }}
              />
            );
          }),
        )}
      </div>

      <div className="flex items-center gap-1 text-[10px] text-faint" aria-hidden="true">
        <span>Less</span>
        {([0, 1, 2, 3, 4] as const).map((level) => (
          <div
            key={level}
            className="size-2.5 rounded-[2px]"
            style={{
              backgroundColor: level === 0 ? theme.grid : withAlpha(base, LEVEL_ALPHA[level]),
            }}
          />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}

/**
 * The out-of-hours 2×2, as a table rather than a chart.
 *
 * A heatmap of four numbers is a table wearing a costume. The shading is a
 * secondary cue on top of the figure itself, which is always shown.
 */
export function TemporalGrid({ distribution }: { distribution: TemporalDistribution }) {
  const theme = useChartTheme();
  const cells = [
    { label: 'Weekday day', value: distribution.weekday_day },
    { label: 'Weekday night', value: distribution.weekday_night },
    { label: 'Weekend day', value: distribution.weekend_day },
    { label: 'Weekend night', value: distribution.weekend_night },
  ];
  const peak = Math.max(1, ...cells.map((cell) => cell.value));

  return (
    <table className="w-full border-collapse text-sm">
      <caption className="sr-only">Entries by weekday or weekend, day or night</caption>
      <thead>
        <tr className="text-xs text-faint">
          <th scope="col" className="p-1 text-left font-medium" />
          <th scope="col" className="p-1 text-right font-medium">
            Day
          </th>
          <th scope="col" className="p-1 text-right font-medium">
            Night
          </th>
          <th scope="col" className="p-1 text-right font-medium">
            Total
          </th>
        </tr>
      </thead>
      <tbody>
        {(
          [
            [
              'Weekday',
              distribution.weekday_day,
              distribution.weekday_night,
              distribution.weekdayTotal,
            ],
            [
              'Weekend',
              distribution.weekend_day,
              distribution.weekend_night,
              distribution.weekendTotal,
            ],
          ] as const
        ).map(([label, day, night, total]) => (
          <tr key={label}>
            <th scope="row" className="p-1 text-left text-xs font-medium text-muted">
              {label}
            </th>
            {[day, night].map((value, index) => (
              <td key={index} className="p-1 text-right">
                <span
                  className="inline-block w-full rounded-sm px-2 py-1 text-text"
                  style={{
                    /*
                     * The shading goes on the background alone, never on the
                     * element. `opacity` here would fade the number along with
                     * its cell, so the quietest cells — the ones a trainee is
                     * most likely to be checking for a zero — became the
                     * hardest to read.
                     *
                     * The alpha tops out at 0.5 because the body text has to
                     * stay above 4.5:1 on the darkest fill it can sit on, which
                     * in the dark theme it does not at the full strength.
                     */
                    backgroundColor: withAlpha(
                      theme.capability[2],
                      0.12 + (value / peak) * 0.38,
                    ),
                  }}
                >
                  {value}
                </span>
              </td>
            ))}
            <td className="p-1 text-right text-muted">{total}</td>
          </tr>
        ))}
        <tr className="text-muted">
          <th scope="row" className="p-1 text-left text-xs font-medium">
            Total
          </th>
          <td className="p-1 text-right">{distribution.dayTotal}</td>
          <td className="p-1 text-right">{distribution.nightTotal}</td>
          <td className="p-1 text-right text-text">{distribution.total}</td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * HiLLO coverage as horizontal bars.
 *
 * Counts only. Never a percentage of a target, never a RAG rating, never a
 * progress bar against `targetLevel` — the app reports activity and the
 * Educational Supervisor judges it. See CURRICULUM.md.
 */
export function HilloBars({ coverage }: { coverage: readonly HilloCoverage[] }) {
  const theme = useChartTheme();
  const peak = Math.max(1, ...coverage.map((row) => row.count));

  return (
    <ul className="flex flex-col gap-1.5">
      {coverage.map((row) => (
        <li key={row.hillo} className="flex items-center gap-2 text-xs">
          <span className="w-4 shrink-0 text-right text-faint">{row.hillo}</span>
          <span className="w-28 shrink-0 truncate text-muted" title={row.shortLabel}>
            {row.shortLabel}
          </span>
          <span className="h-3 min-w-0 flex-1 rounded-sm bg-surface-sunken">
            <span
              className="block h-3 rounded-sm"
              style={{
                width: `${String(Math.max(row.count === 0 ? 0 : 4, (row.count / peak) * 100))}%`,
                background: theme.capability[2],
              }}
            />
          </span>
          <span className={cx('w-8 text-right', row.count === 0 ? 'text-faint' : 'text-text')}>
            {row.count}
          </span>
        </li>
      ))}
    </ul>
  );
}
